import { useAtom } from "jotai";
import { useMemo, useEffect } from "react";
import { granularityPreferenceAtom } from "@/atoms";
import {
  getGranularityForRange,
  getAvailableGranularityOptions,
  getGranularityOptionsWithStatus,
  getEffectiveGranularity,
  type GranularityPreference,
  type GranularityOption,
} from "@lib/util-functions/granularity";
import type { Granularity } from "@lib/api-hooks/useKpiBreakdownForChannel";
import { useDateRangeState } from "@lib/hooks/url-state";

interface UseGranularityReturn {
  /** The auto-calculated granularity based on date range */
  autoGranularity: Granularity;
  /** User's selected granularity preference */
  preference: GranularityPreference;
  /** The effective granularity to use in API calls */
  effectiveGranularity: Granularity;
  /** Available options user can select (enabled only) */
  availableOptions: Granularity[];
  /** All options with enabled/disabled status and reasons */
  allOptionsWithStatus: GranularityOption[];
  /** Function to update preference */
  setPreference: (value: GranularityPreference) => void;
}

export function useGranularity(): UseGranularityReturn {
  const { startTime, endTime } = useDateRangeState();
  const [preference, setPreference] = useAtom(granularityPreferenceAtom);

  const autoGranularity = useMemo(
    () => getGranularityForRange(startTime, endTime),
    [startTime, endTime]
  );

  const allOptionsWithStatus = useMemo(
    () => getGranularityOptionsWithStatus(autoGranularity, startTime, endTime),
    [autoGranularity, startTime, endTime]
  );

  const availableOptions = useMemo(
    () =>
      allOptionsWithStatus.filter((opt) => opt.enabled).map((opt) => opt.value),
    [allOptionsWithStatus]
  );

  const effectiveGranularity = useMemo(
    () =>
      getEffectiveGranularity(preference, autoGranularity, startTime, endTime),
    [preference, autoGranularity, startTime, endTime]
  );

  // Auto-reset if current selection becomes invalid
  useEffect(() => {
    if (!availableOptions.includes(preference)) {
      setPreference(autoGranularity);
    }
  }, [preference, availableOptions, autoGranularity, setPreference]);

  return {
    autoGranularity,
    preference,
    effectiveGranularity,
    availableOptions,
    allOptionsWithStatus,
    setPreference,
  };
}
